import * as Sentry from '@sentry/react';

import { router } from './router';

// Build the trace propagation targets based on environment
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const tracePropagationTargets: (string | RegExp)[] = [/^\//]; // Always trace relative URLs
if (apiBaseUrl) {
  tracePropagationTargets.push(new RegExp(`^${apiBaseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV,
  release: import.meta.env.VITE_APP_VERSION,
  // Off on purpose: this would attach request headers and the user's IP to
  // every event. Users are identified by id instead.
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: false,
  // One Sentry project serves every app; this tag tells the admin web's events apart.
  initialScope: { tags: { app: 'admin-web' } },
  ignoreErrors: [
    // TanStack Router (defaultViewTransition: true) starts a view transition without
    // catching the rejection. Navigating in a hidden tab makes the browser skip it,
    // which rejects harmlessly. Regex covers each browser's wording (Chrome/Safari/Firefox).
    /view ?transition.*\b(skipped|hidden)\b/i,
    // Same cause, different wording: Chrome aborts an in-flight transition with a
    // bare `InvalidStateError` when the document goes hidden, is detached, or a
    // second navigation supersedes the first. The message never says "view
    // transition", so the regex above does not catch it.
    /Transition was aborted because of invalid state/i,
    // Same cause again: a second navigation supersedes the running transition.
    // Here "ViewTransition" comes after "skipped", so the first regex misses it.
    /Transition was skipped/i,
  ],
  integrations: [
    // If you're using react router, use the integration for your react router version instead.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.browserTracingIntegration(),
    Sentry.feedbackIntegration({
      // Additional SDK configuration goes in here, for example:
      colorScheme: 'system',
    }),
    Sentry.tanstackRouterBrowserTracingIntegration(router),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: import.meta.env.VITE_ENV === 'production' ? 0.1 : 1.0,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets,
});
